import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { Plus, Trash2, Save } from 'lucide-react';
import dashboardAPI from '../../services/dashboard';
import { branchAPI } from '../../services/branch';

/**
 * Modal quản lý chi phí cố định hàng tháng (tiền nhà, tiền điện, ...) cho mỗi chi nhánh.
 *
 * - Nếu `selectedBranch` có giá trị: chỉ chỉnh chi nhánh đó.
 * - Nếu KHÔNG chọn chi nhánh (admin view tổng): hiển thị dropdown để chọn chi nhánh cần chỉnh.
 *
 * Lưu trữ ở Configuration table, key=DASHBOARD_FIXED_COSTS, JSON array per branch.
 */
const FixedCostsModal = ({ show, onHide, selectedBranch, onSaved }) => {
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState(selectedBranch?.id || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const genId = () => `fc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  // Load branches list once
  useEffect(() => {
    if (!show) return;
    if (selectedBranch?.id) return; // branch picker fixed
    branchAPI
      .getBranches({ limit: 100, is_active: true })
      .then((res) => {
        const list = res?.data || res?.branches || res || [];
        setBranches(Array.isArray(list) ? list : []);
      })
      .catch(() => setBranches([]));
  }, [show, selectedBranch]);

  // Sync active branch when prop changes
  useEffect(() => {
    if (selectedBranch?.id) setActiveBranchId(selectedBranch.id);
  }, [selectedBranch]);

  const loadItems = useCallback(async (branchId) => {
    if (!branchId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getFixedCosts({ branch_id: branchId });
      const group = (res?.branches || [])[0];
      const loaded = (group?.items || []).map((it) => ({
        id: it.id || genId(),
        name: it.name || '',
        monthly_amount: Number(it.monthly_amount) || 0,
        note: it.note || '',
      }));
      setItems(loaded);
    } catch (e) {
      setError(e?.response?.data?.message || 'Không tải được chi phí cố định');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show && activeBranchId) loadItems(activeBranchId);
  }, [show, activeBranchId, loadItems]);

  const handleAdd = () => {
    setItems((prev) => [
      ...prev,
      { id: genId(), name: '', monthly_amount: 0, note: '' },
    ]);
  };

  const handleRemove = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              [field]: field === 'monthly_amount' ? Number(value) || 0 : value,
            }
          : it,
      ),
    );
  };

  const handleSave = async () => {
    if (!activeBranchId) {
      setError('Vui lòng chọn chi nhánh');
      return;
    }
    const cleanItems = items
      .map((it) => ({
        ...it,
        name: (it.name || '').trim(),
      }))
      .filter((it) => it.name.length > 0 && it.monthly_amount >= 0);

    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await dashboardAPI.updateFixedCosts({
        branch_id: activeBranchId,
        items: cleanItems,
      });
      setInfo('Đã lưu chi phí cố định');
      if (typeof onSaved === 'function') onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  const totalMonthly = items.reduce(
    (sum, it) => sum + (Number(it.monthly_amount) || 0),
    0,
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Chi phí cố định hàng tháng</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="small mb-3">
          Khai báo các khoản chi phí cố định (tiền nhà, tiền điện, internet...). Khi xem
          dashboard, hệ thống sẽ <strong>chia tỷ lệ theo số ngày</strong> trong kỳ (mặc
          định 30 ngày = 1 tháng) và cộng vào tổng chi phí.
        </Alert>

        {!selectedBranch?.id && (
          <Form.Group className="mb-3">
            <Form.Label>Chi nhánh</Form.Label>
            <Form.Select
              value={activeBranchId}
              onChange={(e) => setActiveBranchId(e.target.value)}
            >
              <option value="">-- Chọn chi nhánh --</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.code ? `(${b.code})` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {selectedBranch?.id && (
          <div className="mb-3">
            <Badge bg="primary">{selectedBranch.name}</Badge>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}
        {info && <Alert variant="success">{info}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Đang tải...
          </div>
        ) : (
          <>
            <Table size="sm" responsive bordered>
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Tên khoản chi</th>
                  <th style={{ width: '25%' }}>Số tiền / tháng (đ)</th>
                  <th>Ghi chú</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      Chưa có khoản chi nào. Bấm "Thêm khoản chi" để bắt đầu.
                    </td>
                  </tr>
                ) : (
                  items.map((it) => (
                    <tr key={it.id}>
                      <td>
                        <Form.Control
                          size="sm"
                          placeholder="Tiền nhà"
                          value={it.name}
                          onChange={(e) =>
                            handleChange(it.id, 'name', e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          step={1000}
                          value={it.monthly_amount}
                          onChange={(e) =>
                            handleChange(it.id, 'monthly_amount', e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          placeholder="Ghi chú (tùy chọn)"
                          value={it.note}
                          onChange={(e) =>
                            handleChange(it.id, 'note', e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleRemove(it.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="text-end"><strong>Tổng/tháng</strong></td>
                    <td colSpan={3}>
                      <strong>{totalMonthly.toLocaleString('vi-VN')} đ</strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>

            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleAdd}
              disabled={!activeBranchId}
            >
              <Plus size={14} /> Thêm khoản chi
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Đóng
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !activeBranchId}
        >
          <Save size={14} /> {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FixedCostsModal;
